import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "../../lib/api";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Cell,
} from "recharts";
import { DollarSign, TrendingUp, AlertTriangle, Users } from "lucide-react";

const STATUS_COLORS = {
  trial: "#8b5cf6",
  active: "#10b981",
  past_due: "#f59e0b",
  suspended: "#ef4444",
  canceled: "#71717a",
};

export default function PlatformBilling() {
  const { t } = useTranslation();
  const [metrics, setMetrics] = useState(null);
  const [series, setSeries] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [m, ts, tn] = await Promise.all([
          api.get("/platform/metrics"),
          api.get("/platform/timeseries?days=60"),
          api.get("/platform/tenants"),
        ]);
        setMetrics(m.data);
        setSeries(ts.data.series || []);
        setTenants(tn.data || []);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const attention = useMemo(
    () =>
      tenants.filter((tn) =>
        ["past_due", "suspended"].includes(tn.subscription_status),
      ),
    [tenants],
  );

  const churnRisk = useMemo(() => {
    // Tenants whose trial ends in <=3 days
    const now = Date.now();
    const in3days = now + 3 * 24 * 60 * 60 * 1000;
    return tenants.filter((t) => {
      if (t.subscription_status !== "trial" || !t.trial_ends_at) return false;
      const ts = new Date(t.trial_ends_at).getTime();
      return !isNaN(ts) && ts <= in3days;
    });
  }, [tenants]);

  const growthMrr = useMemo(() => {
    if (series.length < 2) return 0;
    return series[series.length - 1].mrr_usd - series[0].mrr_usd;
  }, [series]);

  if (loading || !metrics) {
    return <div className="h-32 animate-pulse rounded-xl bg-zinc-200/60" />;
  }

  const statusBar = [
    { name: "trial", value: metrics.trial, fill: STATUS_COLORS.trial },
    { name: "active", value: metrics.active, fill: STATUS_COLORS.active },
    { name: "past_due", value: metrics.past_due, fill: STATUS_COLORS.past_due },
    { name: "suspended", value: metrics.suspended, fill: STATUS_COLORS.suspended },
    { name: "canceled", value: metrics.canceled, fill: STATUS_COLORS.canceled },
  ];

  return (
    <div className="space-y-6" data-testid="platform-billing">
      <header>
        <div className="text-[11px] font-semibold uppercase tracking-[0.24em] text-violet-600">Serpix Admin</div>
        <h1 className="mt-1 font-display text-3xl font-semibold tracking-tight text-zinc-950 sm:text-4xl">
          {t("platform.nav.billing")}
        </h1>
        <p className="mt-1 text-sm text-zinc-500">
          {t("platform.billing.subtitle")}
        </p>
      </header>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <KpiBig
          label="MRR"
          value={`$${metrics.mrr_usd.toLocaleString()}`}
          hint={`${metrics.active} paid × $19`}
          icon={DollarSign}
          testId="platform-billing-mrr"
          growth={growthMrr}
        />
        <KpiBig
          label={t("platform.billing.arr")}
          value={`$${(metrics.mrr_usd * 12).toLocaleString()}`}
          hint="MRR × 12"
          icon={TrendingUp}
          testId="platform-billing-arr"
        />
        <KpiBig
          label={t("platform.billing.arpu")}
          value={
            metrics.total_users > 0
              ? `$${(metrics.mrr_usd / metrics.total_users).toFixed(2)}`
              : "$0"
          }
          hint={`${metrics.total_users} users`}
          icon={Users}
          testId="platform-billing-arpu"
        />
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="lg:col-span-2 rounded-xl border border-zinc-200 bg-white p-5">
          <h3 className="mb-3 text-sm font-semibold text-zinc-900">
            {t("platform.billing.mrr_60d")}
          </h3>
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={series} margin={{ left: -10, right: 8, top: 8, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="date" tickFormatter={(d) => d.slice(5)} stroke="#a1a1aa" fontSize={11} />
              <YAxis stroke="#a1a1aa" fontSize={11} tickFormatter={(v) => `$${v}`} />
              <Tooltip
                contentStyle={{ borderRadius: 10, border: "1px solid #e4e4e7" }}
                formatter={(v) => `$${v}`}
              />
              <Line
                type="monotone"
                dataKey="mrr_usd"
                stroke="#10b981"
                strokeWidth={2.5}
                dot={{ r: 3, fill: "#10b981" }}
                activeDot={{ r: 5 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="rounded-xl border border-zinc-200 bg-white p-5">
          <h3 className="mb-3 text-sm font-semibold text-zinc-900">
            {t("platform.billing.subscription_mix")}
          </h3>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={statusBar}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="name" stroke="#a1a1aa" fontSize={11} />
              <YAxis stroke="#a1a1aa" fontSize={11} />
              <Tooltip contentStyle={{ borderRadius: 10, border: "1px solid #e4e4e7" }} />
              <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                {statusBar.map((s, i) => (
                  <Cell key={i} fill={s.fill} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <AttentionList
          title={t("platform.billing.at_risk")}
          items={attention}
          icon={AlertTriangle}
          tone="red"
          empty={t("platform.billing.no_at_risk")}
          testId="platform-billing-at-risk"
        />
        <AttentionList
          title={t("platform.billing.trial_ending")}
          items={churnRisk}
          icon={TrendingUp}
          tone="amber"
          empty={t("platform.billing.no_trial_ending")}
          testId="platform-billing-trial-ending"
        />
      </div>
    </div>
  );
}

function KpiBig({ label, value, hint, icon: Icon, testId, growth }) {
  return (
    <div
      className="relative overflow-hidden rounded-xl border border-zinc-200 bg-white p-6"
      data-testid={testId}
    >
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.24em] text-zinc-500">
            {label}
          </div>
          <div className="mt-2 font-display text-3xl font-bold text-zinc-950">
            {value}
          </div>
          <div className="mt-1 text-[11px] text-zinc-500">
            {hint}
            {growth !== undefined && growth !== 0 && (
              <span className={growth > 0 ? "ml-2 text-emerald-600" : "ml-2 text-red-600"}>
                {growth > 0 ? "+" : ""}${growth.toLocaleString()} 60d
              </span>
            )}
          </div>
        </div>
        <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 text-white shadow-md">
          <Icon className="h-5 w-5" strokeWidth={2} />
        </div>
      </div>
    </div>
  );
}

function AttentionList({ title, items, icon: Icon, tone, empty, testId }) {
  const toneMap = {
    red: "border-red-200 from-red-50 to-orange-50 text-red-800",
    amber: "border-amber-200 from-amber-50 to-orange-50 text-amber-800",
  };
  return (
    <div
      className={`rounded-xl border bg-gradient-to-br p-5 ${toneMap[tone] || toneMap.amber}`}
      data-testid={testId}
    >
      <div className="mb-3 flex items-center gap-2">
        <Icon className="h-4 w-4" />
        <h3 className="font-semibold">{title}</h3>
        <span className="ml-auto rounded-full bg-white/70 px-2 py-0.5 text-[11px] font-semibold">
          {items.length}
        </span>
      </div>
      {items.length === 0 ? (
        <p className="text-sm opacity-70">{empty}</p>
      ) : (
        <ul className="space-y-1.5 max-h-80 overflow-y-auto">
          {items.map((tn) => (
            <li
              key={tn.id}
              className="flex items-center justify-between rounded-md bg-white/70 px-3 py-2 text-xs"
            >
              <div className="min-w-0">
                <div className="truncate font-medium text-zinc-900">{tn.name}</div>
                <div className="truncate text-[10px] text-zinc-500 font-mono">
                  {tn.subscription_status}
                  {tn.trial_ends_at && ` · ends ${new Date(tn.trial_ends_at).toLocaleDateString()}`}
                </div>
              </div>
              <span className="ml-2 text-zinc-600">{tn.user_count}u · {tn.task_count}t</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
